import { PdfDownloadPayload } from "../../types/types";

const INNER_BORDER_COLOR = "#d9d9d9";
const CARD_HEADER_BG = "#fff8f3";
/** TCPDF ignores CSS padding on td/th; use the table cellpadding attribute instead (mm). */
const TABLE_CELL_PADDING = "3";
const TABLE_ATTRS = `cellpadding="${TABLE_CELL_PADDING}" cellspacing="0" width="100%"`;
const INNER_BORDER = `border-bottom:1px solid ${INNER_BORDER_COLOR};`;
const INNER_BORDER_TOP = `border-top:1px solid ${INNER_BORDER_COLOR};`;
const INNER_BORDER_LEFT = `border-left:1px solid ${INNER_BORDER_COLOR};`;
const INNER_BORDER_RIGHT = `border-right:1px solid ${INNER_BORDER_COLOR};`;

const CARD_WRAPPER_STYLE = "width:100%;border-collapse:collapse;border:none;";
const CARD_WRAPPER_CELL_STYLE = "border:none;";
const CARD_INNER_TABLE_STYLE =
	"width:100%;border-collapse:collapse;border:none;margin:0;font-size:10px;";

const PRODUCT_TABLE_STYLE = `width:100%;border-collapse:collapse;font-size:10px;margin:0;border:1px solid ${INNER_BORDER_COLOR};`;

const CARD_PRODUCT_STYLE = "";
const CARD_SPACER_STYLE =
	"width:100%;height:24px;line-height:24px;font-size:24px;";

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function buildProductCellStyle(
	colIndex: number,
	colCount: number,
	isHeader: boolean,
	isLastRow = false,
): string {
	const isFirstColumn = colIndex === 0;
	const isLastColumn = colIndex === colCount - 1;
	const styleParts = ["white-space:nowrap", "vertical-align:top"];

	if (!isLastRow) {
		styleParts.push(INNER_BORDER);
	}

	if (isHeader) {
		styleParts.push(
			INNER_BORDER_TOP,
			"font-weight:bold",
			"background-color:#f5f5f5",
		);
	}

	if (isFirstColumn) {
		styleParts.push(INNER_BORDER_LEFT);
	}

	if (isLastColumn) {
		styleParts.push(INNER_BORDER_RIGHT);
	}

	return `${styleParts.join(";")};`;
}

function buildProductTableHtml(table: HTMLTableElement): string {
	const headers = Array.from(table.querySelectorAll("thead th")).map(
		(cell) => cell.textContent?.trim() ?? "",
	);
	const bodyRows = Array.from(table.querySelectorAll("tbody tr")).map((row) =>
		Array.from(row.querySelectorAll("td")).map(
			(cell) => cell.textContent?.trim() || "—",
		),
	);

	if (headers.length === 0 || bodyRows.length === 0) {
		return "";
	}

	const headerRow = headers
		.map(
			(header, colIndex) =>
				`<th style="${buildProductCellStyle(colIndex, headers.length, true)}">${escapeHtml(header)} </th>`,
		)
		.join("\n\t\t\t\t");

	const dataRows = bodyRows
		.map((cells, rowIndex) => {
			const isLastRow = rowIndex === bodyRows.length - 1;
			const cellsHtml = cells
				.map(
					(cell, colIndex) =>
						`<td style="${buildProductCellStyle(colIndex, cells.length, false, isLastRow)}">${escapeHtml(cell)}</td>`,
				)
				.join("\n\t\t\t\t");

			return `
			<tr>
				${cellsHtml}
			</tr>`;
		})
		.join("");

	return `
		<table ${TABLE_ATTRS} style="${PRODUCT_TABLE_STYLE}">
			<thead style="border:1px solid ${INNER_BORDER_COLOR};">
				<tr border="1" bordercolor="${INNER_BORDER_COLOR}">
					${headerRow}
				</tr>
			</thead>
			<tbody>
				${dataRows}
			</tbody>
		</table>`;
}

function buildSeriesCardHtml(card: Element, productTableHtml: string): string {
	const seriesName =
		card.querySelector(".pf-series-card__name")?.textContent?.trim() ?? "";
	const specSpans = card.querySelectorAll(".pf-series-card__specs span");
	const metricSpans = card.querySelectorAll(".pf-series-card__metrics span");
	const hp = specSpans[0]?.textContent?.trim() || "—";
	const discharge = specSpans[1]?.textContent?.trim() || "—";
	const gpm = metricSpans[0]?.textContent?.trim() || "—";
	const tdh = metricSpans[1]?.textContent?.trim() || "—";
	const productRow =
		productTableHtml !== ""
			? `
		<tr>
			<td colspan="2" style="${CARD_PRODUCT_STYLE}">
				${productTableHtml}
			</td>
		</tr>`
			: "";

	return `
	<table cellpadding="0" cellspacing="0" width="100%" style="${CARD_WRAPPER_STYLE}">
		<tr>
			<td style="${CARD_WRAPPER_CELL_STYLE}">
				<table ${TABLE_ATTRS} style="${CARD_INNER_TABLE_STYLE}">
				<tr><td>
					<table cellpadding="4" cellspacing="4" width="100%" style="background-color:${CARD_HEADER_BG};border:1px solid ${INNER_BORDER_COLOR};" >
						<tr>
							<td colspan="2" width="100%" >
								<b>${escapeHtml(seriesName)}</b>
							</td>
						</tr>
						<tr>
							<td width="50%">
								${escapeHtml(hp)}<br />
								${escapeHtml(discharge)}
							</td>
							<td width="50%">
								${escapeHtml(gpm)}<br />
								${escapeHtml(tdh)}
							</td>
						</tr>
					</table></td></tr>
			${productRow}
				</table>
			</td>
		</tr>
	</table>`;
}

function isSeriesSwitchOn(card: Element): boolean {
	const switchInput = card.querySelector(
		".pf-switch__input",
	) as HTMLInputElement | null;

	return switchInput?.checked === true;
}

function buildSeriesTable(seriesSelector: HTMLElement): string {
	const cards = Array.from(
		seriesSelector.querySelectorAll(".pf-series-card"),
	).filter(isSeriesSwitchOn);

	if (cards.length === 0) {
		return "";
	}

	return cards
		.map((card) => {
			const seriesCode = card.getAttribute("data-series-code") ?? "";
			const expandedTable = card.querySelector(
				".pf-series-card__details .pf-series-table",
			);
			const pdfTable = seriesCode
				? seriesSelector.querySelector(
					`.pf-series-pdf-tables [data-series-code="${seriesCode}"] .pf-series-table`,
				)
				: null;
			const table = (pdfTable ?? expandedTable) as HTMLTableElement | null;
			const productTableHtml = table ? buildProductTableHtml(table) : "";

			return buildSeriesCardHtml(card, productTableHtml);
		})
		.join(
			`<table cellpadding="0" cellspacing="0" width="100%" style="${CARD_SPACER_STYLE}"><tr><td>&nbsp;</td></tr></table>`,
		);
}

function getChartImageDataUrl(root: HTMLElement): string | null {
	const canvas = root.querySelector(
		".pf-pump-curve-chart canvas",
	) as HTMLCanvasElement | null;

	if (!canvas) {
		return null;
	}

	return canvas.toDataURL("image/png");
}

export function extractPdfContent(savedResultId?: string): PdfDownloadPayload {
	const root = document.getElementById("pump-sizing-root");
	if (!root) {
		throw new Error("Pump sizing content is not available.");
	}

	const seriesSelector =
		root.querySelector(".pf-series-selector") ??
		document.querySelector(".pf-series-selector");

	const chartImage = getChartImageDataUrl(root);
	if (!chartImage) {
		throw new Error("Pump curve chart is not available to download.");
	}

	const seriesHtml =
		seriesSelector instanceof Element
			? buildSeriesTable(seriesSelector as HTMLElement)
			: "";

	return {
		seriesHtml,
		chartImage,
		...(savedResultId ? { savedResultId } : {}),
	};
}
